---
title: Workspace settings
lede: One workspace's repositories, and the application settings it answers differently.
order: 46
part: Teams and settings
---

Workspace settings hold what belongs to one workspace — its name, its repositories, its services and tunnels — and let it override most [application settings](/settings). Open them with `⌘⇧,`, the last icon at the foot of the navigator, or **Workspace settings…** in the Workspace menu.

![The workspace settings rail](media/ws-sections.webp)

The Workspace menu also opens a page directly: **Repositories…**, **Session image…**, **Agent…**, **Pull requests…**, **Network and egress…**, **Tunnels…** and **Services on this computer…**.

Saving works as in application settings: **Save** (`⌘S`), **Revert**, **Done**.

### Overriding a setting

![Inherited settings](media/ws-agent.webp)

A row that follows the application shows the tag **APP** and the application's value.

1. Press **Override** on the row. The tag becomes **THIS WORKSPACE** and the value becomes editable.
2. Change the value. The row says what it replaces, for example *Instead of 30 days from the application.*
3. Press **Reset** to give the setting back to the application.

Every setting you have not overridden keeps following the application, including later changes. A row you overrode is tagged **THIS WORKSPACE**.

In a workspace shared by an [organization](/organization), a setting the organization defaults is tagged with its name instead of APP, and you can still override it. A setting the organization enforces shows a padlock, its value, and no verb.

### Pages

| Page | What it holds |
|---|---|
| **General** | **Name**, **Color**, the **Workspace root** (a display prefix), and **Delete workspace**. See [Workspaces](/workspaces). |
| **Repositories** | The workspace's projects, their commands, ports and environment. See [Repositories](/repositories). |
| **Rules** | Only with a Team or Enterprise seat: the rules repository, where it is cloned, and each project's folder name. See [Rules](/rules). |
| **Organization** | Only in an organization: sharing, sync and history. See [Organization](/organization). |
| **Targets** | The environments this workspace's code runs against, their variables and secrets. See [Targets](/targets). |
| **Sessions** | Overrides **Sessions start on**, **Source branch** and **Keep archived tasks for**. |
| **Agent** | Overrides **Run autonomously**, **Mask personal data**, **Reuse the agent's config from this computer**, **Agent version**, **Idle fallback**, **Let the agent drive Preview**, **…using this workspace's saved logins** and **Live probes**. **Names this workspace masks** adds to the application's list. |
| **Memory** | What every agent of this workspace keeps in mind. Workspace only. See [Workspace memory](/memory). |
| **Sandbox** | Overrides **Sessions run in**, **Session image**, **Container runtime**, **Docker disk**, **CPU cores**, **Memory** and **Disk**. |
| **Skills, MCP & plugins** | Replaces the application's list as a whole for this workspace. |
| **Network** | Hosts this workspace adds, hosts never asked about, and **Unlisted hosts**. See [Network](/network). |
| **Services** | Services on this computer the sessions may reach. Workspace only. See [Services](/services). |
| **Tunnels** | **Public URLs for this workspace**. Off by default. See [Tunnels](/tunnels). |
| **Git & merge** | Overrides **Credit the agent in the commit**, **Sync when the agent hands back**, **Pull request integration** and **When a session is finished**. |
| **Audit** | Overrides the audit as a whole: **Audit each hand-back**, **Change Map drawn with**, **Hold the merge on rules** and **Hold the merge on findings**. |
| **Notifications** | Overrides **Interrupt me when a session**. |
| **Logs** | Overrides **Retention per log file** and **Lines kept in the dock**. |

![Workspace sandbox settings](media/ws-sandbox.webp)

**Sessions run in** picks where new sessions of this workspace start: **Micro-VM when available**, or **This computer — git worktrees**. **Session image** offers **Generate**, which writes an image from the versions your repositories pin.

On **Network**, **Discover hosts** installs your repositories in a sandbox behind the egress gate and lists the hosts they reached. Hosts added here add to the application's list; they never remove from it.

### What stays application-wide

A workspace cannot override the two session ceilings, **Park an idle machine after**, the workflow defaults, the resolvers, **Images kept**, anything on General, the agents' own pages, or the integrations. These describe this computer, not one workspace. See [Defaults and limits](/limits).

### Good reasons to override

- A workspace holding riskier repositories turns **Run autonomously** off.
- A workspace you release from pins **Agent version**, so sessions a week apart behave the same.
- A workspace whose repositories need your computer's devices sets **Sessions run in** to this computer.
- A workspace with heavier repositories raises **Memory** or **CPU cores**.
- A workspace with no pull requests turns **Pull request integration** off.
- A workspace whose code needs a closer look draws its Change Map with a larger model and holds the merge on **High and Medium** findings.
